import { Component, OnInit, inject } from '@angular/core';
import { Domain } from '../../../shared/classes/domain';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CrudService } from '../../crud.service';
import { FormGroup, FormControl } from '@angular/forms';
import { LoginService } from '../../../shared/services/login.service';
import { DefaultDialogConfig } from '../../../shared/classes/default-dialog-config';
import { DnsVerificationStatus } from '../../../shared/classes/dns-verification';
import { DomainStatus } from '../../../shared/enums/domain-status.enum';
import { firstValueFrom } from 'rxjs';


interface VerifyDomainDialogData {
  domain: Domain;
}

export class VerifyDomainDialogConfig extends DefaultDialogConfig<VerifyDomainDialogData> {
  public disableClose = false;

  constructor(domain: Domain) {
    super();
    this.data = { domain };
  }
}

export enum VerifyDomainDialogOutcome {
  DnsSuccess = 'DNS_SUCCESS',
}

@Component({
  templateUrl: './verify-domain-dialog.component.html',
  styleUrls: ['./verify-domain-dialog.component.scss'],
  standalone: false
})
export class VerifyDomainDialogComponent implements OnInit {
  private readonly data = inject<VerifyDomainDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<VerifyDomainDialogData>>(MatDialogRef);
  private readonly crudService = inject(CrudService);
  private readonly loginService = inject(LoginService);

  public readonly RESEND_MSG_DELAY_MS: number = 5 * 1000; // 5 seconds
  public readonly DomainStatus = DomainStatus;

  public busy = false;
  public fetchDnsCodeError = false;
  public verifyDnsError = false;
  public verificationTxtNotFoundError = false;
  public domain = this.data.domain;

  public readonly dnsForm = new FormGroup({
    verificationCode: new FormControl(''),
  });
  public get verificationCode() { return this.dnsForm.controls.verificationCode; }

  public async ngOnInit(): Promise<void> {
    await this.getVerificationCode();
  }

  public onCancel() {
    this.dialogRef.close();
  }

  public async getVerificationCode(): Promise<void> {
    this.fetchDnsCodeError = false;
    try {
      const code = await firstValueFrom(this.crudService.getDnsVerificationCode(
        this.loginService.user.enterpriseId, this.domain.id), { defaultValue: undefined });
      this.verificationCode.setValue(code);
    } catch (e) {
      console.log("Failed to get verification code:", e)
      this.fetchDnsCodeError = true;
    }
  }

  public async verifyDnsRecord(): Promise<void> {
    this.busy = true;
    this.verifyDnsError = false;
    this.verificationTxtNotFoundError = false;
    try {
      const status = await firstValueFrom(this.crudService.verifyDomainByDns(
        this.loginService.user.enterpriseId, this.domain.id), { defaultValue: undefined });
      this.handleDnsVerificationStatus(status);
    } catch (e) {
      console.log("Failed to verify DNS record:", e)
      this.verifyDnsError = true;
    } finally {
      this.busy = false;
    }
  }

  private handleDnsVerificationStatus(status: DnsVerificationStatus) {
    switch (status) {
      case DnsVerificationStatus.Passed:
        this.dialogRef.close(VerifyDomainDialogOutcome.DnsSuccess);
        break;
      case DnsVerificationStatus.Failed:
        this.verificationTxtNotFoundError = true;
        break;
    }
  }
}
